import React, { useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import Spinner from "../layout/Spinner";

import Post from "./Post";
import PostSkeleton from "./PostSkeleton";

//Actions
import { getPostData } from "../../actions/feed";

//Selectors
import { getPostWithCacheData, isPostDataCached } from "../../selectors/postSelectors";

const PostManager = ({ index, getPostData, feed }) => {
  const currentFeedType = feed.feeds?.currentFeedType;
  const currentFeed = feed.feeds?.[currentFeedType];
  const posts = currentFeed?.posts;
  const loading = currentFeed?.loading;

  // Get post with cache data
  const postWithCache = getPostWithCacheData(feed, currentFeedType, index);

  useEffect(() => {
    if (!postWithCache) {
      return;
    }

    const validId = postWithCache.postid || postWithCache.id || postWithCache._id;
    if (!validId) {
      return;
    }

    // Check if post is already cached (has postData)
    const isCached = !!postWithCache.postData;

    // Check if post has an error
    const hasError = postWithCache.error;

    // Check if post is in error cooldown period (2 minutes)
    const errorTimestamp = feed.postCache.errors[validId];
    const isInErrorCooldown = errorTimestamp && Date.now() - errorTimestamp < 2 * 60 * 1000;

    // Post should be treated as "loading" if:
    // 1. Not cached (no postData)
    // 2. Not in error cooldown
    // 3. Not already has an error
    const shouldBeLoading = !isCached && !hasError && !isInErrorCooldown;

    // Fetch if post should be loading (not cached, no error, not in cooldown)
    if (shouldBeLoading) {
      getPostData(validId, index, postWithCache.repostuser || null);
    }
  }, [
    postWithCache?.postid,
    postWithCache?.error,
    postWithCache?.postData,
    index,
    currentFeedType,
    getPostData,
    feed.postCache.errors,
  ]);

  if (!postWithCache) {
    return null;
  }

  // Determine if post should show loading state
  const validId = postWithCache.postid || postWithCache.id || postWithCache._id;
  const isCached = !!postWithCache.postData;
  const hasError = postWithCache.error;
  const isLoading = postWithCache.loading;
  const errorTimestamp = feed.postCache.errors[validId];
  const isInErrorCooldown = errorTimestamp && Date.now() - errorTimestamp < 2 * 60 * 1000;

  // Show skeleton only if not cached and not in error cooldown
  const shouldShowSkeleton = !isCached && !hasError && !isInErrorCooldown;

  // Show Post component if we have cached data (even if loading) or if no error
  const shouldShowPost = isCached || (!hasError && !isInErrorCooldown);

  // Prepare post data for the Post component
  const postForComponent = {
    ...postWithCache,
    postData: postWithCache.postData || null, // Ensure postData is available
    loading: isLoading, // Pass loading state to Post component
  };

  return (
    <Fragment>
      {shouldShowSkeleton ? (
        <PostSkeleton />
      ) : hasError ? null : shouldShowPost ? (
        <Post post={postForComponent} />
      ) : null}
    </Fragment>
  );
};

PostManager.propTypes = {
  getPostData: PropTypes.func.isRequired,
  feed: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  feed: state.feed,
});
export default connect(mapStateToProps, { getPostData })(PostManager);
